import { SupplierInvoiceType, SupplierPaymentType, TransactionMethod } from '@cpce/database/enums';
import z from 'zod';
import { startOfDay, zDateField } from '../utils/date.utils.js';
import { purchaseLineShape, refinePurchaseLine, signedUnitPriceSchema, } from './purchase-lines.types.js';
export const DEFAULT_CHARGE_ACCOUNT_CODE = '471000';
export const DEFAULT_BANK_ACCOUNT_CODE = '512000';
export const SupplierInvoiceStatus = {
    paid: 'paid',
    proforma: 'proforma',
    pending: 'pending',
    partiallyPaid: 'partiallyPaid',
    // An avoir is never refunded: it is « imputé », deducted from later invoices of its supplier.
    toImpute: 'toImpute',
    partiallyImputed: 'partiallyImputed',
    imputed: 'imputed',
};
export const INVOICE_STATUS_LABELS = {
    [SupplierInvoiceStatus.paid]: 'Réglé',
    [SupplierInvoiceStatus.proforma]: 'Proforma',
    [SupplierInvoiceStatus.pending]: 'En attente de règlement',
    [SupplierInvoiceStatus.partiallyPaid]: 'Réglée partiellement',
    [SupplierInvoiceStatus.toImpute]: 'À imputer',
    [SupplierInvoiceStatus.partiallyImputed]: 'Partiellement imputé',
    [SupplierInvoiceStatus.imputed]: 'Imputé',
};
export const supplierInvoiceWithRelations = {
    include: {
        supplier: {
            select: {
                id: true,
                name: true,
                code: true,
                paymentMethod: true,
                chargeAccount: { select: { id: true, code: true, name: true } },
            },
        },
        lines: {
            orderBy: { position: 'asc' },
            include: {
                prestation: { select: { id: true, name: true } },
                prestationPrice: { select: { id: true, name: true, price: true } },
                accountingCategories: { select: { id: true, name: true, parentId: true } },
                imputedCreditNote: { select: { id: true, reference: true } },
                transactionAccountMovement: {
                    include: {
                        financialAccount: { select: { id: true, code: true, name: true } },
                    },
                },
            },
        },
        orders: {
            select: { id: true, reference: true },
        },
        // Not `baseTransaction`: its movements embed every lettrage group member, and an accrual
        // lettered against a big 401 group would make this payload heavy.
        accrualTransaction: {
            select: {
                id: true,
                reference: true,
                date: true,
                description: true,
                status: true,
                journal: { select: { code: true } },
                transactionAccountMovements: {
                    select: {
                        id: true,
                        amount: true,
                        transactionType: true,
                        financialAccount: { select: { code: true } },
                    },
                },
            },
        },
        // The facture's règlements go through its allocations: `amount` is the share of the écriture
        // charged to **this** facture, whereas summing the écriture's movements would attribute the
        // cheque's total to every facture it settles.
        allocations: { select: { amount: true, transaction: { select: { cancelledAt: true } } } },
        imputations: {
            orderBy: { invoice: { issueDate: 'asc' } },
            select: {
                id: true,
                quantity: true,
                unitPrice: true,
                invoice: { select: { id: true, reference: true, issueDate: true } },
            },
        },
    },
};
export const supplierInvoiceLineDataSchema = z
    .object({
    ...purchaseLineShape,
    unitPrice: signedUnitPriceSchema,
    id: z.number().int().optional(),
    orderId: z.number().int().optional().nullable(),
    // Ignored on a line imputing an avoir: it is booked on the supplier's account.
    chargeAccountCode: z.string().default(DEFAULT_CHARGE_ACCOUNT_CODE),
    imputedCreditNoteId: z.number().int().positive().nullable().default(null),
})
    .superRefine((line, ctx) => {
    refinePurchaseLine(line, ctx);
    refineCreditNoteImputation(line, ctx);
});
export const CREDIT_NOTE_IMPUTATION_SIGN_ERROR = "Le montant imputé sur l'avoir doit être négatif";
export function refineCreditNoteImputation(line, ctx) {
    if (line.imputedCreditNoteId === null) {
        if (line.unitPrice !== undefined && line.unitPrice < 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'Le prix unitaire doit être positif',
                path: ['unitPrice'],
            });
        }
        return;
    }
    if (line.prestationId !== null) {
        ctx.addIssue({
            code: 'custom',
            message: "Une imputation d'avoir est une ligne libre, sans prestation",
            path: ['prestationId'],
        });
    }
    if (line.unitPrice === undefined || line.unitPrice >= 0) {
        ctx.addIssue({
            code: 'custom',
            message: CREDIT_NOTE_IMPUTATION_SIGN_ERROR,
            path: ['unitPrice'],
        });
    }
}
export function creditNoteImputationLabel(creditNoteReference) {
    return `Imputation avoir ${creditNoteReference}`;
}
export const supplierInvoiceDataSchema = z.object({
    supplierId: z.number().int().positive({ message: 'Le fournisseur est requis' }),
    type: z.enum(SupplierInvoiceType).default(SupplierInvoiceType.invoice),
    reference: z.string({ message: 'La référence est requise' }).min(1, 'La référence est requise'),
    issueDate: zDateField("La date d'émission est requise"),
    dueDate: zDateField("La date d'échéance est requise"),
    // Omitted: the comité's purchases journal.
    journalCode: z.string().optional(),
    // An invoice is an accrual (401/6xx) with no treasury movement, so it carries no mode de
    // règlement: the treasury account (SR4) is chosen at règlement time, not here.
    lines: z.array(supplierInvoiceLineDataSchema).min(1, 'Au moins une ligne est requise'),
    orderIds: z.array(z.number().int()).default([]),
});
export const partialSupplierInvoiceDataSchema = z.object({
    supplierId: z.number().int().positive({ message: 'Le fournisseur est requis' }).optional(),
    reference: z.string().min(1, 'La référence est requise').optional(),
    issueDate: zDateField("La date d'émission est requise").optional(),
    dueDate: zDateField("La date d'échéance est requise").optional(),
    journalCode: z.string().optional(),
    lines: z.array(supplierInvoiceLineDataSchema).optional(),
    orderIds: z.array(z.number().int()).optional(),
});
export const bulkDeleteInvoicesSchema = z.object({
    ids: z.array(z.number().int()),
});
// PCG class 471 ("compte d'attente") is the suspense account backing proforma invoices.
// An order/invoice created against '471000' falls back to the committee's active 471*
// account when that exact code is absent (imported plans often use '471'), so proforma
// detection must match the whole 471 prefix — not just the seed code '471000'.
export const PROFORMA_ACCOUNT_PREFIX = '471';
export function isProformaChargeAccountCode(code) {
    return code?.startsWith(PROFORMA_ACCOUNT_PREFIX) ?? false;
}
// By calendar day: an avoir keyed the same day as the invoice is not « after » it, whatever the hour
// an older client stamped on it.
export function isCreditNoteDatedAfter(creditNoteIssueDate, invoiceIssueDate) {
    return startOfDay(new Date(creditNoteIssueDate)) > startOfDay(new Date(invoiceIssueDate));
}
export const openCreditNotesQuerySchema = z.object({
    // The invoice being edited: its own imputations do not count against what is left.
    excludeInvoiceId: z.coerce.number().int().positive().optional(),
});
export const validateSupplierInvoiceLineSchema = z.object({
    lineId: z.number().int().positive(),
    chargeAccountCode: z.string().min(1, 'Le compte de charge est requis'),
});
export const validateSupplierInvoiceSchema = z.object({
    lines: z.array(validateSupplierInvoiceLineSchema).min(1, 'Au moins une ligne est requise'),
});
export const changeInvoiceLineChargeAccountSchema = z.object({
    financialAccountId: z.number().int().positive('Le compte de charge est requis'),
});
export const setInvoiceLineCategorySchema = z.object({
    accountingCategoryId: z.number().int().positive().nullable(),
});
// The modes de règlement a CSE pays a supplier by, validated by the client (2026-09-01). A subset
// of `TransactionMethod`, which stays the single vocabulary of payment means across the app: a
// preset on an account and an instrument on a payment must never both say "Chèque" through two
// different enums.
export const SUPPLIER_PAYMENT_INSTRUMENTS = [
    TransactionMethod.check,
    TransactionMethod.bankTransfer,
    TransactionMethod.bankCard,
    TransactionMethod.directDebit,
];
export const supplierPaymentSettlementItemSchema = z.object({
    invoiceId: z.number().int().positive(),
    amount: z
        .number()
        .positive({ message: 'Le montant doit être positif' })
        .multipleOf(0.01, { message: 'Le montant ne peut pas avoir plus de deux décimales' }),
    paymentDate: z.coerce.date(),
    label: z.string().min(1, "L'intitulé est requis"),
    // The treasury account credited by this settlement's écriture (a `decaissementBancaire` mode).
    // Chosen inline per row rather than once for the whole batch.
    bankAccountCode: z.string().min(1, 'Le compte de décaissement est requis'),
    // The instrument the money leaves by. It belongs to the payment, not to the account: a cheque, a
    // virement and a card payment all credit the same 512, and only this tells them apart.
    instrument: z.enum(SUPPLIER_PAYMENT_INSTRUMENTS, {
        message: 'Le mode de règlement est requis',
    }),
    // N° de chèque ou équivalent. Offered as soon as the instrument is a cheque, never required:
    // unlike a cheque received — physically in hand when it is recorded — a supplier cheque is often
    // printed in a batch and numbered afterwards. Blank counts as absent.
    instrumentNumber: z
        .union([z.string().min(1), z.literal('').transform(() => undefined)])
        .optional(),
});
// No journal in the payload: each settlement écriture posts on its mode de règlement's associated
// journal (the bank journal for a 512 outflow mode), like every other treasury écriture.
export const supplierPaymentSettlementSchema = z.object({
    supplierId: z.number().int().positive({ message: 'Le fournisseur est requis' }),
    type: z.enum(SupplierPaymentType).default(SupplierPaymentType.reglement),
    settlements: z
        .array(supplierPaymentSettlementItemSchema)
        .min(1, 'Au moins un règlement est requis'),
});
export const correctSupplierPaymentSchema = z.object({
    financialAccountId: z
        .number({ message: 'Le compte de décaissement est requis' })
        .int()
        .positive('Le compte de décaissement est requis'),
    instrument: z.enum(SUPPLIER_PAYMENT_INSTRUMENTS, {
        message: 'Le mode de règlement est requis',
    }),
    instrumentNumber: z
        .union([z.string().min(1), z.literal('').transform(() => undefined)])
        .optional(),
    paymentDate: zDateField('La date du règlement est requise'),
});
export const cancelSupplierPaymentsSchema = z.object({
    transactionIds: z.array(z.number().int().positive()).min(1, 'Au moins un règlement est requis'),
});
